import { useContext, useMemo } from 'react';

// project-imports
import RolesContext from 'contexts/RolesContext';
import { ROLE_OPTIONS as FALLBACK, roleLabel as formatCode } from 'utils/roles';

// Central access to the (API-driven) role list. Falls back to the static master
// while the API response is in flight or if it fails, so pickers are never empty.
//   roleOptions  – [{ value, code, roleCode, label, roleLabel }] for any picker
//   roleCodes    – ['SUPER_ADMIN', ...]
//   roleLabels   – { code: label }
//   roleLabel(c) – label for a code (custom label, else formatted code)
export default function useRoles() {
  const ctx = useContext(RolesContext);

  return useMemo(() => {
    const live = (ctx?.roles || []).filter((r) => r?.active !== false);
    const source = live.length
      ? live.map((r) => ({ code: r.code, label: r.label || formatCode(r.code) }))
      : FALLBACK.map((o) => ({ code: o.code, label: o.label }));

    const roleOptions = source.map((r) => ({
      value: r.code,
      code: r.code,
      roleCode: r.code,
      label: r.label,
      roleLabel: r.label,
    }));
    const roleCodes = source.map((r) => r.code);
    const roleLabels = source.reduce((m, r) => {
      m[r.code] = r.label;
      return m;
    }, {});
    const roleLabel = (code) => roleLabels[code] || formatCode(code);

    return {
      roles: ctx?.roles || [],
      loading: !!ctx?.loading,
      refresh: ctx?.refresh || (() => {}),
      roleOptions,
      roleCodes,
      roleLabels,
      roleLabel,
    };
  }, [ctx]);
}
